import React, { useEffect } from 'react';
import {
  BaseInputProps,
  Dropdown,
  DropdownLayoutTheme,
  LoadingAnimation,
  Option,
  SimpleBlock,
  useFormContext,
  useInputHelper
} from 'ui-builder';
import { TeamSetupArea, TeamSetupAreaStatus, useTeamSetupAreasService } from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import useFeatureToggleService, { NewTransactionsManagementToggleWrapper } from 'feature-toggle';
import { useStatesHelper } from '../states';

interface BaseTeamSetupAreaDropdownProps<DataType extends string | number> extends BaseInputProps<DataType> {
  defaultValue?: DataType,
  getValueFunc: (item: TeamSetupArea) => DataType,
  withAllOption?: boolean;
  isCompact?: boolean
}

function BaseTeamSetupAreaDropdown<DataType extends string | number>(props: BaseTeamSetupAreaDropdownProps<DataType>) {
  const teamSetupAreaService = useTeamSetupAreasService();

  const { data } = useDataLoaderNew(teamSetupAreaService.getTeamSetupAreas);

  const inputHelper = useInputHelper<DataType>(props);

  const value = inputHelper.getValue();

  const formContext = useFormContext();

  const statesHelper = useStatesHelper();

  const changeValue = (newValue: DataType | undefined) => {
    inputHelper.getOnChangeCallback()({
      target: {
        value: newValue
      }
    });
  };

  useEffect(() => {
    if (data && !props.withAllOption) {
      let defaultValue;

      if (props.defaultValue) {
        defaultValue = props.defaultValue;
      } else if (typeof props.value === 'string' || typeof props.value === 'number') {
        defaultValue = props.value as DataType;
      }
      if (defaultValue) {
        changeValue(defaultValue as (DataType | undefined));
      } else {
        const defaultArea = data.find((item) => item.isDefault);
        if (defaultArea) {
          changeValue(props.getValueFunc(defaultArea));
        } else {
          changeValue(undefined);
        }

      }
    }
  }, [data?.length]);

  useEffect(() => {
    const setupArea = data?.find((item) => props.getValueFunc(item) === value);

    formContext?.onChangeCallback?.({
      _hasSets: setupArea?.hasSets,
      _areaProjectType: setupArea?.projectType,
      detailsFieldsSetId: undefined,
    });

  }, [value]);

  const featureToggleService = useFeatureToggleService();

  if (data && data.length > 1) {

    const options = [
      ...props.withAllOption ? [{
        value: null,
        label: 'All Markets'
      }] : [],
      ...data
        .filter((item) => item.status === TeamSetupAreaStatus.ACTIVE)
        .map((item) => {
        const state = item.market?.state || '';
        let label;
        if (state) {
          label = `${item.name} - ${statesHelper.getLabelByValue(state, true)}`;
        } else {
          label = item.name;
        }

        return {
          value: props.getValueFunc(item),
          label: label
        };
      })
    ] as Option<DataType>[];

    return (
      <NewTransactionsManagementToggleWrapper hideInDevelopment>
        <Dropdown<DataType>
          {...props}
          label={props.label || featureToggleService.isCustomMarketsEnabled() ? 'Market / Process' : 'Market'}
          options={options}
          isDisabled={data.length === 1}
          isClearable={false}
          layoutTheme={props.isCompact ? DropdownLayoutTheme.COMPACT : DropdownLayoutTheme.STANDARD}
        />
      </NewTransactionsManagementToggleWrapper>
    );
  } else {
    if (!data) {
      return (<LoadingAnimation />);
    } else {
      return null;
    }
  }
}

export interface TeamSetupAreaDropdownProps<DataType extends number | string> extends BaseInputProps<DataType> {
  defaultValue?: DataType;
  withAllOption?: boolean;
  isCompact?: boolean;
}

export function TeamSetupAreaAliasDropdown(props: TeamSetupAreaDropdownProps<string>) {
  return (
    <SimpleBlock className="form">
      <BaseTeamSetupAreaDropdown<string>
        {...props}
        getValueFunc={(item) => item.alias}
        withAllOption={props.withAllOption}
      />
    </SimpleBlock>

  );
}

export function TeamSetupAreaIdDropdown(props: TeamSetupAreaDropdownProps<number>) {
  return (
    <BaseTeamSetupAreaDropdown<number>
      {...props}
      getValueFunc={(item) => item.id}
      withAllOption={props.withAllOption}
      isCompact={props.isCompact}
    />
  );
}
